/**
 * Attendance API + TanStack Query hooks.
 *
 * Mirrors the patterns established in @features/calendar/api/sessions.ts:
 * - typed via the generated OpenAPI schema (`@api/index`)
 * - centralised query-key factory
 * - mutations invalidate the roster + the session detail
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@core/api/client';
import type { components } from '@api/index';

// NOTE: The entitlement cache key namespace `['catalog', 'entitlements']`
// is duplicated here intentionally — Phase 2.5 forbids feature-to-feature
// imports, so we cannot pull `entitlementKeys.all` from
// `@features/catalog/api/entitlements`. If the catalog feature ever
// renames the namespace, the M7 review item recurs and this comment is
// the canonical place to look. Promoting query-key factories into
// `@core/` would resolve this cleanly when we touch the boundary again.
const ENTITLEMENT_CACHE_NAMESPACE = ['catalog', 'entitlements'] as const;

// Same cross-feature-key caveat as above: `['calendar','sessions']` is owned
// by `@features/calendar/api/sessions` and duplicated here because the
// import boundary forbids reaching for `sessionKeys.all` directly.
const CALENDAR_SESSIONS_CACHE_NAMESPACE = ['calendar', 'sessions'] as const;

export type AttendanceResponse = components['schemas']['AttendanceResponse'];
export type AttendanceRosterResponse =
  components['schemas']['AttendanceRosterResponse'];
export type AttendanceRosterEntry = components['schemas']['AttendanceRosterEntry'];
export type AttendanceUpdate = components['schemas']['AttendanceUpdate'];
export type BulkAttendanceRequest = components['schemas']['BulkAttendanceRequest'];
export type BulkAttendanceResponse = components['schemas']['BulkAttendanceResponse'];
export type BulkAttendanceItemResult =
  components['schemas']['BulkAttendanceItemResult'];
export type AttendanceStatus = AttendanceResponse['status'];
export type AttendanceEntryRequest =
  components['schemas']['AttendanceEntryRequest'];

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const attendanceKeys = {
  all: ['attendance'] as const,
  roster: (sessionId: string) => ['attendance', 'roster', sessionId] as const,
  detail: (id: string) => ['attendance', 'detail', id] as const,
};

// ---------------------------------------------------------------------------
// REST callers
// ---------------------------------------------------------------------------

export async function getSessionAttendance(
  sessionId: string,
): Promise<AttendanceRosterResponse> {
  return api.get<AttendanceRosterResponse>(
    `/v1/sessions/${sessionId}/attendance`,
  );
}

export async function markAttendance(
  sessionId: string,
  entries: AttendanceEntryRequest[],
): Promise<BulkAttendanceResponse> {
  return api.post<BulkAttendanceResponse>(
    `/v1/sessions/${sessionId}/attendance`,
    { entries } satisfies BulkAttendanceRequest,
  );
}

export async function updateAttendance(
  attendanceId: string,
  body: AttendanceUpdate,
): Promise<AttendanceResponse> {
  return api.patch<AttendanceResponse>(`/v1/attendance/${attendanceId}`, body);
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useSessionAttendance(sessionId: string | undefined) {
  return useQuery({
    queryKey: attendanceKeys.roster(sessionId ?? '__none__'),
    queryFn: () => getSessionAttendance(sessionId as string),
    enabled: Boolean(sessionId),
    staleTime: 15_000,
  });
}

export function useMarkAttendance(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entries: AttendanceEntryRequest[]) =>
      markAttendance(sessionId, entries),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: attendanceKeys.roster(sessionId) });
      // Entitlement counters shifted — refresh any open student-entitlements view.
      qc.invalidateQueries({ queryKey: ENTITLEMENT_CACHE_NAMESPACE });
      // 11E.7: the first successful mark stamps `session.attendance_marked_at`,
      // which the calendar renders as the check icon and the session page
      // uses to disable Delete. Both read the sessions cache, not the roster.
      qc.invalidateQueries({ queryKey: CALENDAR_SESSIONS_CACHE_NAMESPACE });
      // Student attendance summaries (11E.6) recount from these rows.
      qc.invalidateQueries({ queryKey: ['student-attendance'] });
    },
  });
}

export function useUpdateAttendance(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: AttendanceUpdate }) =>
      updateAttendance(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: attendanceKeys.roster(sessionId) });
      qc.invalidateQueries({ queryKey: ENTITLEMENT_CACHE_NAMESPACE });
    },
  });
}

